/**
 * Firebase Configuration
 *
 * Initializes Firebase app with environment variables.
 * Uses lazy initialization to avoid errors when Firebase is disabled.
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app'
import { connectAuthEmulator, getAuth, Auth } from 'firebase/auth'
import { getFirestore, Firestore } from 'firebase/firestore'

type RuntimeImportMeta = ImportMeta & {
  env?: Record<string, string | undefined>
}

const runtimeEnv = (import.meta as RuntimeImportMeta).env
const firebaseConfig = {
  apiKey: runtimeEnv?.VITE_FIREBASE_API_KEY || '',
  authDomain: runtimeEnv?.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: runtimeEnv?.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: runtimeEnv?.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: runtimeEnv?.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: runtimeEnv?.VITE_FIREBASE_APP_ID || '',
}
// Environments where an emulator is a legitimate target. Anything else --
// including an unset VITE_ENVIRONMENT -- is treated as deployed and refuses,
// because the failure is silent and total: `connectAuthEmulator` rewrites
// every auth call to `http://<host>/identitytoolkit.googleapis.com/...`,
// which the page CSP then blocks, and the user sees only
// `auth/network-request-failed`.
//
// This is not hypothetical. A developer's gitignored .env carrying
// VITE_FIREBASE_AUTH_EMULATOR_HOST=127.0.0.1:9143 was picked up by Vite
// during a staging build on 2026-09-23 and shipped to every portal; nobody
// could sign in to staging until it was found.
const LOCAL_ENVIRONMENTS = new Set(['development', 'dev', 'local', 'test'])

/**
 * The emulator host to honour, or '' to use real Firebase.
 *
 * Exported so the rule is testable without importing the module for its
 * side effects; `config.ts` reads `import.meta.env` at load time, which a
 * test cannot vary.
 */
export const resolveAuthEmulatorHost = (
  env: Record<string, unknown> | undefined,
): string => {
  const requested = String(env?.VITE_FIREBASE_AUTH_EMULATOR_HOST ?? '')
  if (!requested) {
    return ''
  }

  // String(... ?? '') rather than `x || ''`: a build with no VITE_ENVIRONMENT
  // must fall through to the deny branch, not throw on undefined and blank
  // the page before anything renders.
  const declared = String(env?.VITE_ENVIRONMENT ?? '').toLowerCase()
  if (Boolean(env?.DEV) || LOCAL_ENVIRONMENTS.has(declared)) {
    return requested
  }

  // Loud on purpose: ignoring it makes the build safe, but the .env that
  // supplied it should still be fixed.
  console.warn(
    `[firebase] Ignoring VITE_FIREBASE_AUTH_EMULATOR_HOST=${requested} for a ` +
      `non-local build (VITE_ENVIRONMENT="${declared || 'unset'}"). ` +
      'Auth will use the real Firebase endpoints.',
  )
  return ''
}

const authEmulatorHost = resolveAuthEmulatorHost(runtimeEnv)

// Check if Firebase is configured
export const isFirebaseConfigured = (): boolean => {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId)
}

// Lazy-initialize Firebase app
let app: FirebaseApp | null = null
let auth: Auth | null = null
let db: Firestore | null = null
let authEmulatorConnected = false

export const getFirebaseApp = (): FirebaseApp | null => {
  if (!isFirebaseConfigured()) {
    return null
  }
  if (!app) {
    app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0]
  }
  return app
}

export const getFirebaseAuth = (): Auth | null => {
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) {
    return null
  }
  if (!auth) {
    auth = getAuth(firebaseApp)
    if (authEmulatorHost && !authEmulatorConnected) {
      const emulatorUrl = authEmulatorHost.startsWith('http')
        ? authEmulatorHost
        : `http://${authEmulatorHost}`
      connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true })
      authEmulatorConnected = true
    }
  }
  return auth
}

export const getFirestoreDb = (): Firestore | null => {
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) {
    return null
  }
  if (!db) {
    db = getFirestore(firebaseApp)
  }
  return db
}

export { firebaseConfig }
